import HyperHTMLElement from 'hyperhtml-element';
import { render } from 'inferno';
import { defaultTemplates, toolbarFormatters } from './toolbarTemplates';
import trimSeparators from './trimSeparators';
import type {
	toolbarItems,
	FormatterT,
	checkedItemT,
	toolbarDataRender
} from './toolbarTypes';

export default class Toolbar extends HyperHTMLElement {
	#leftContainerItems: string[] = [];
	#rightContainerItems: string[] = [];
	#renderingPromise: Promise<void> | null = null;
	#resizeObserver = new ResizeObserver(() => this.render());
	#templates = defaultTemplates;

	datastore: Map<string, toolbarItems> = new Map();
	focusedItemId = '';
	declare expanded: boolean;
	declare overflow: boolean;
	declare vertical: boolean;
	declare role: string;

	static get booleanAttributes(): string[] {
		return ['overflow', 'expanded', 'vertical'];
	}
	static get observedAttributes(): string[] {
		return ['role'];
	}

	_getItemTabindex(itemId: string) {
		if (this._isToolbarRole()) {
			return itemId === this.focusedItemId ? '0' : '-1';
		}

		return this._isItemFocusable(itemId) ? '0' : '-1';
	}

	_getDisabledAttributeName() {
		return this._isToolbarRole() ? 'aria-disabled' : 'disabled';
	}

	#focusoutHandler = (event: FocusEvent): void => {
		const relatedTarget = event.relatedTarget;
		if (
			this.expanded &&
			!(relatedTarget instanceof Node && this.contains(relatedTarget))
		) {
			this.expanded = false;
		}
	};

	#resizeHandler = (): Promise<void> => {
		return this.render();
	};

	static extendFormatters(formatter: FormatterT): void {
		Object.assign(toolbarFormatters, formatter);
	}

	connectedCallback(): void {
		if (!this.role) {
			this.role = 'toolbar';
		}
		if (!this.overflow && this._isToolbarRole()) {
			this.overflow = true;
		}

		this._initKeyboardHandler();
		this._initOverflowHandlers();
		this.on('click', (parentItemId, event) =>
			this._checkedItemClickHandler(parentItemId, event)
		);
		window.addEventListener('resize', this.#resizeHandler);
	}

	disconnectedCallback(): void {
		this.#resizeObserver.unobserve(this);
		window.removeEventListener('resize', this.#resizeHandler);
	}

	attributeChangedCallback(attrName: string): void {
		if (attrName === 'role') {
			this._initKeyboardHandler();
		} else if (attrName === 'overflow') {
			this._initOverflowHandlers();
		} else if (attrName === 'vertical') {
			if (this.vertical) {
				this.setAttribute('aria-orientation', 'vertical');
			} else {
				this.removeAttribute('aria-orientation');
			}
		}

		this.render();
	}

	private _initKeyboardHandler() {
		if (this._isToolbarRole()) {
			this.addEventListener('keydown', this._keyboardEventHandler);
		} else {
			this.removeEventListener('keydown', this._keyboardEventHandler);
		}
	}

	private _initOverflowHandlers() {
		if (this.overflow) {
			this.#resizeObserver.observe(this);
			this.addEventListener('focusout', this.#focusoutHandler);
		} else {
			this.#resizeObserver.unobserve(this);
			this.removeEventListener('focusout', this.#focusoutHandler);
		}
	}

	private async _keyboardEventHandler(e: KeyboardEvent) {
		if (e.ctrlKey || e.altKey || e.shiftKey || e.metaKey) {
			return;
		}

		const prevItemKey = this.vertical ? 'ArrowUp' : 'ArrowLeft';
		const nextItemKey = this.vertical ? 'ArrowDown' : 'ArrowRight';
		const key: string = e.code;
		if (![prevItemKey, nextItemKey].includes(key)) {
			return;
		}
		if (
			e.target instanceof Element &&
			this._getFocusNode() !== e.target &&
			!e.target.closest('.aras-toolbar__expand-button')
		) {
			return;
		}

		let nextItemId;
		switch (key) {
			case prevItemKey:
				nextItemId = this._getPrevFocusable();
				break;
			case nextItemKey:
				nextItemId = this._getNextFocusable();
				break;
		}

		if (nextItemId) {
			e.preventDefault();
			await this._focusItem(nextItemId);

			// preventDefault isn't working for 'select' in FF: https://bugzilla.mozilla.org/show_bug.cgi?id=1019630
			const targetNode = e.target;

			if (!(targetNode instanceof HTMLElement)) {
				return;
			}
			if (
				targetNode.nodeName === 'SELECT' &&
				/Firefox\/(\S+)/.test(window.navigator.userAgent)
			) {
				const originalDisplay = targetNode.style.display;
				targetNode.style.display = 'none';
				targetNode.clientHeight; // execute re-rendering of HTML engine
				targetNode.style.display = originalDisplay;
				// don't call anything below what will re-render HTML
			}
		}
	}

	private _getFocusableItems() {
		return [...this.#leftContainerItems, ...this.#rightContainerItems].filter(
			(itemId) => this._isItemFocusable(itemId)
		);
	}

	private _getNextFocusable() {
		const itemsId = this._getFocusableItems();
		if (!itemsId.length) {
			return '';
		}

		return itemsId[itemsId.indexOf(this.focusedItemId) + 1] || itemsId[0];
	}

	private _getPrevFocusable() {
		const itemsId = this._getFocusableItems();
		if (!itemsId.length) {
			return '';
		}

		return (
			itemsId[itemsId.indexOf(this.focusedItemId) - 1] ||
			itemsId[itemsId.length - 1]
		);
	}

	private async _focusItem(itemId = '') {
		this.focusedItemId = itemId;
		await this.render();
		const focusNode = this._getFocusNode();
		if (!focusNode) {
			return;
		}

		if (focusNode.closest('.aras-toolbar__item_hidden')) {
			this.expanded = true;
			await this.render();
		}

		focusNode.focus();
	}

	private _getFocusNode() {
		if (!this.focusedItemId) {
			return null;
		}
		const dataIdSelector = `[data-id="${this.focusedItemId}"]`;
		return this.querySelector<HTMLElement>(
			`${dataIdSelector}[tabindex="0"], ${dataIdSelector} [tabindex="0"]`
		);
	}

	private _isItemFocusable(itemId: string) {
		const item = this.datastore.get(itemId);

		return (
			item &&
			!item.hidden &&
			!(item.attributes && item.attributes['aria-hidden']) &&
			item.type !== 'separator' &&
			(!item.disabled || ['button', 'dropdownMenu'].includes(item.type))
		);
	}

	private async _checkedItemClickHandler(parentItemId: string, event: Event) {
		const node = event.target;
		if (!(node instanceof Element)) {
			return;
		}

		const targetNode = node.closest<HTMLElement>('li[data-index]');
		const parentItem = this.data.get(parentItemId);

		if (!targetNode?.dataset.index || !(parentItem && 'data' in parentItem)) {
			return;
		}

		const dataMap = parentItem.data;
		const targetNodeIndex = targetNode.dataset.index;
		const targetItem = dataMap.get(targetNodeIndex) as checkedItemT;

		if (
			targetItem &&
			!targetItem.disabled &&
			(targetItem.checked !== undefined || targetItem.type === 'checkbox')
		) {
			const groupId = targetItem.group_id;
			if (groupId) {
				for (const [key, item] of dataMap) {
					if (!('group_id' in item && item.group_id === groupId)) {
						continue;
					}
					dataMap.set(key, { ...item, checked: false });
				}
				targetItem.checked = true;
			} else {
				targetItem.checked = !targetItem.checked;
			}

			dataMap.set(targetNodeIndex, { ...targetItem });
			this.data.set(parentItemId, { ...parentItem });
			return this.render();
		}
	}

	private _overflowRender() {
		const options = this._getVnodeProperties();
		options.options = Object.assign(options.options, {
			isOverflowVisible: true,
			rightContainerOrder: -1
		});
		this._render(options);

		let rightContainerOrder = this._getLastItemOnFirstRowIndex();
		if (this._hasItemsInOverflow()) {
			options.options = Object.assign(options.options, {
				isExpandButtonHidden: true
			});
			this._render(options);

			if (!this._hasItemsInOverflow()) {
				rightContainerOrder = this._getLastItemOnFirstRowIndex();
			}
		}

		const finalOptions = this._getVnodeProperties();
		finalOptions.options.rightContainerOrder = rightContainerOrder;
		this._render(finalOptions);

		if (
			!this.expanded &&
			this.firstElementChild &&
			this.offsetParent !== null
		) {
			this.style.height = this.firstElementChild.clientHeight + 'px';
		}
	}

	private _render(options?: toolbarDataRender) {
		if (
			this._isToolbarRole() &&
			(!this.focusedItemId || !this._isItemFocusable(this.focusedItemId))
		) {
			this.focusedItemId = this._getNextFocusable();
		}

		const root = this.#templates.root(options || this._getVnodeProperties());
		render(root, this);
	}

	render(): Promise<void> {
		if (this.#renderingPromise) {
			return this.#renderingPromise;
		}
		this.#renderingPromise = Promise.resolve().then(() => {
			if (this.overflow) {
				this._overflowRender();
			} else {
				this._render();
			}

			this.#renderingPromise = null;

			if (this.expanded && !this.contains(document.activeElement)) {
				this.expanded = false;
			}
		});
		return this.#renderingPromise;
	}

	set data(dataMap: Map<string, toolbarItems>) {
		this.datastore = dataMap;
		this.#rightContainerItems = [];
		this.#leftContainerItems = [];
		this.datastore.forEach((value, key) => {
			if (value.right) {
				this.#rightContainerItems.push(key);
			} else {
				this.#leftContainerItems.push(key);
			}
		});
		this.render();
	}

	get data(): Map<string, toolbarItems> {
		return this.datastore;
	}

	set container(newContainerItems: string[]) {
		this.#leftContainerItems = newContainerItems;
		this.render();
	}

	get container(): string[] {
		return this.#leftContainerItems;
	}

	set rightContainer(newContainerItems: string[]) {
		this.#rightContainerItems = newContainerItems;
		this.render();
	}

	get rightContainer(): string[] {
		return this.#rightContainerItems;
	}

	on(
		eventType: string,
		callback: (itemId: string, event: Event) => void | Promise<void>
	): () => void {
		const handler = (event: Event) => {
			const targetItemId = this._getIdByDomElement(event.target);
			const item = this.data.get(targetItemId);
			if (item && !item.disabled) {
				callback(targetItemId, event);
			}
		};
		this.addEventListener(eventType, handler);
		return () => {
			this.removeEventListener(eventType, handler);
		};
	}

	protected _getIdByDomElement(element: EventTarget | null): string {
		if (!(element instanceof Element)) {
			return '';
		}
		const targetNodeSelector = '[data-id]';
		const targetNode = element.closest<HTMLElement>(targetNodeSelector);
		return targetNode?.dataset.id ?? '';
	}

	private _getVnodeProperties(): toolbarDataRender {
		const leftContainerItems = trimSeparators(
			this.data,
			this.#leftContainerItems
		);
		const rightContainerItems = trimSeparators(
			this.data,
			this.#rightContainerItems
		);
		return {
			leftContainerItems,
			rightContainerItems,
			toolbar: this,
			options: {
				isToolbarRole: this._isToolbarRole(),
				isOverflowAllowed: this.overflow,
				isOverflowVisible: this.expanded,
				isExpandButtonHidden: false,
				isVertical: this.vertical,
				rightContainerOrder: leftContainerItems.length
			}
		};
	}

	private _isToolbarRole() {
		return this.role === 'toolbar';
	}

	private _getLastItemOnFirstRowIndex() {
		const maximalFirstRowItemOffsetTop = 20;
		const lastItemOnFirstRowIndex = Array.prototype.findIndex.call(
			this.firstElementChild && this.firstElementChild.childNodes,
			(itemNode) => itemNode.offsetTop > maximalFirstRowItemOffsetTop
		);

		return lastItemOnFirstRowIndex === -1
			? this.#leftContainerItems.length
			: lastItemOnFirstRowIndex - 1;
	}

	private _hasItemsInOverflow() {
		return this.firstElementChild
			? this.clientHeight < this.firstElementChild.clientHeight
			: false;
	}

	_toggleToolbarExpandState() {
		this.expanded = !this.expanded;

		if (this.expanded) {
			this._focusItem(this.focusedItemId);
		}
	}
}
